/**
 * Survey validation — docs/superpowers/specs/2026-10-08-surveys-part1.md §2, §6.
 *
 * - parseSurveyInput: shape check on every save (drafts may have empty
 *   Hindi text).
 * - publishProblems: what still blocks publishing (both languages, options…).
 * - isLockedChange: once live, question structure can't change (text can).
 */
import { z } from "zod";
import { isValidSemver } from "../semver";
import { QUESTION_TYPES } from "./types";
import type {
  Localized,
  SurveyInput,
  SurveyQuestion,
  SurveyStatus,
} from "./types";

const TEXT_MAX = 500;

const localized = z.object({
  en: z.string().max(TEXT_MAX),
  hi: z.string().max(TEXT_MAX),
});

const option = z.object({
  key: z.string().regex(/^o_[0-9a-z]{6}$/, "Invalid option key"),
  text: localized,
});

const question = z
  .object({
    id: z.string().regex(/^q_[0-9a-z]{8}$/, "Invalid question id"),
    type: z.enum(QUESTION_TYPES as [string, ...string[]]),
    text: localized,
    required: z.boolean(),
    options: z.array(option).max(20).optional(),
    allowOther: z.boolean().optional(),
    maxSelect: z.number().int().min(1).optional(),
    rating: z
      .object({
        scale: z.number().int().min(2).max(10),
        style: z.enum(["faces", "stars"]),
      })
      .optional(),
    nps: z.object({ lowLabel: localized, highLabel: localized }).optional(),
  })
  .superRefine((q, ctx) => {
    const choice = q.type === "single_choice" || q.type === "multi_choice";
    if (choice && !q.options) {
      ctx.addIssue({ code: "custom", message: `${q.id}: choice question needs options` });
    }
    if (q.options) {
      const keys = q.options.map((o) => o.key);
      if (new Set(keys).size !== keys.length) {
        ctx.addIssue({ code: "custom", message: `${q.id}: duplicate option key` });
      }
    }
    if (q.type === "multi_choice" && q.maxSelect != null && q.options) {
      const max = q.options.length + (q.allowOther ? 1 : 0);
      if (q.maxSelect > max) {
        ctx.addIssue({ code: "custom", message: `${q.id}: maxSelect above the number of choices` });
      }
    }
    if (q.type === "rating" && !q.rating) {
      ctx.addIssue({ code: "custom", message: `${q.id}: rating question needs a scale` });
    }
    if (q.type === "nps" && !q.nps) {
      ctx.addIssue({ code: "custom", message: `${q.id}: NPS question needs labels` });
    }
  });

const content = z.object({
  intro: z.object({ title: localized, body: localized }),
  thankYou: z.object({ title: localized, body: localized }),
  feedback: z.object({
    enabled: z.boolean(),
    prompt: localized,
    maxChars: z.number().int().min(1).max(1000),
  }),
});

const days = z.number().int().min(1).max(3650);

const audience = z
  .object({
    activeWithinDays: days.optional(),
    inactiveForDays: days.optional(),
    minExpenses: z.number().int().min(1).max(100000).optional(),
    minAccountAgeDays: days.optional(),
    maxAccountAgeDays: days.optional(),
    group: z.enum(["any", "shared", "personal_only"]).optional(),
    platforms: z.array(z.enum(["ios", "android"])).optional(),
    minAppVersion: z
      .string()
      .refine(isValidSemver, "App version must look like 1.0.5")
      .optional(),
    languages: z.array(z.enum(["en", "hi"])).optional(),
    samplePercent: z.number().int().min(1).max(100).optional(),
  })
  .strict();

const isoDate = z
  .string()
  .refine((s) => !Number.isNaN(Date.parse(s)), "Invalid date")
  .nullable();

const surveyInput = z
  .object({
    title: z.string().trim().min(1, "Name is required").max(120),
    content,
    questions: z.array(question).max(30),
    audience,
    startAt: isoDate,
    endAt: isoDate,
    responseLimit: z.number().int().min(1).max(1_000_000).nullable(),
    maxShows: z.number().int().min(1).max(20),
    showCooldownHours: z.number().int().min(0).max(24 * 90),
    maxDismissals: z.number().int().min(1).max(20),
    testEmails: z
      .array(z.string().trim().toLowerCase().pipe(z.email("Invalid test email")))
      .max(50)
      .transform((list) => Array.from(new Set(list))),
  })
  .superRefine((s, ctx) => {
    const ids = s.questions.map((q) => q.id);
    if (new Set(ids).size !== ids.length) {
      ctx.addIssue({ code: "custom", message: "Duplicate question id" });
    }
    if (s.startAt && s.endAt && Date.parse(s.endAt) <= Date.parse(s.startAt)) {
      ctx.addIssue({ code: "custom", message: "End must be after start" });
    }
  });

/** Audience on its own (live count while editing). */
export function parseAudience(
  body: unknown,
): { ok: true; value: SurveyInput["audience"] } | { ok: false; errors: string[] } {
  const r = audience.safeParse(body ?? {});
  if (r.success) return { ok: true, value: r.data as SurveyInput["audience"] };
  return { ok: false, errors: r.error.issues.map((i) => `${i.path.join(".")}: ${i.message}`) };
}

export type ParseResult =
  | { ok: true; value: SurveyInput }
  | { ok: false; errors: string[] };

export function parseSurveyInput(body: unknown): ParseResult {
  const r = surveyInput.safeParse(body);
  if (r.success) return { ok: true, value: r.data as SurveyInput };
  return {
    ok: false,
    errors: r.error.issues.map((i) =>
      i.path.length ? `${i.path.join(".")}: ${i.message}` : i.message,
    ),
  };
}

function missingLangs(l: Localized): string[] {
  const out: string[] = [];
  if (!l.en.trim()) out.push("English");
  if (!l.hi.trim()) out.push("Hindi");
  return out;
}

/** Everything that still blocks publishing — empty means ready. */
export function publishProblems(s: SurveyInput): string[] {
  const problems: string[] = [];
  const need = (label: string, l: Localized) => {
    for (const lang of missingLangs(l)) problems.push(`${label}: missing ${lang}`);
  };

  if (s.questions.length === 0) problems.push("Add at least one question");

  need("Intro title", s.content.intro.title);
  need("Intro text", s.content.intro.body);
  need("Thank-you title", s.content.thankYou.title);
  need("Thank-you text", s.content.thankYou.body);
  if (s.content.feedback.enabled) need("Feedback prompt", s.content.feedback.prompt);

  s.questions.forEach((q, i) => {
    const n = `Q${i + 1}`;
    need(`${n} text`, q.text);
    const choice = q.type === "single_choice" || q.type === "multi_choice";
    if (choice) {
      if ((q.options?.length ?? 0) < 2) problems.push(`${n}: add at least 2 options`);
      q.options?.forEach((o, j) => need(`${n} option ${j + 1}`, o.text));
    }
    if (q.type === "rating" && q.rating?.style === "faces" && q.rating.scale !== 5) {
      problems.push(`${n}: faces need a 5-point scale`);
    }
    if (q.type === "nps" && q.nps) {
      need(`${n} low label`, q.nps.lowLabel);
      need(`${n} high label`, q.nps.highLabel);
    }
  });
  return problems;
}

/**
 * The parts of the questions that existing answers depend on. Text and
 * rating style may change on a live survey; this may not.
 */
export function structureSignature(questions: SurveyQuestion[]): string {
  return JSON.stringify(
    questions.map((q) => [
      q.id,
      q.type,
      q.required,
      (q.options ?? []).map((o) => o.key),
      q.allowOther ?? false,
      q.maxSelect ?? null,
      q.rating?.scale ?? null,
    ]),
  );
}

export function isLockedChange(
  status: SurveyStatus,
  before: SurveyQuestion[],
  after: SurveyQuestion[],
): boolean {
  if (status === "draft") return false;
  return structureSignature(before) !== structureSignature(after);
}
